// Zadanie zamkniete E8: przeksztalcanie wzoru na obwod.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import SvgMathLabel from "../../../../components/diagrams/SvgMathLabel";

const LETTERS = ["A", "B", "C", "D"];

const SHAPES = {
  concavePentagon: {
    points: "48,22 158,54 145,126 38,116 76,76",
    labels: [
      { x: 107, y: 27 },
      { x: 160, y: 92 },
      { x: 92, y: 137 },
      { x: 43, y: 94 },
      { x: 50, y: 49 },
    ],
    coefficientSets: [[2, 2, 1], [3, 1, 1]],
  },
  convexPentagon: {
    points: "88,18 164,60 142,130 55,132 20,64",
    labels: [
      { x: 130, y: 28 },
      { x: 164, y: 99 },
      { x: 99, y: 141 },
      { x: 27, y: 104 },
      { x: 49, y: 34 },
    ],
    coefficientSets: [[2, 2, 1], [3, 1, 1]],
  },
  convexHexagon: {
    points: "52,20 130,18 176,60 158,122 82,136 20,94",
    labels: [
      { x: 91, y: 12 },
      { x: 159, y: 37 },
      { x: 176, y: 94 },
      { x: 121, y: 139 },
      { x: 45, y: 124 },
      { x: 18, y: 57 },
    ],
    coefficientSets: [[3, 2, 1], [2, 2, 2]],
  },
};

function pick(values) {
  return values[Math.floor(Math.random() * values.length)];
}

function shuffle(values) {
  const result = [...values];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function termLatex(coefficient, variable) {
  return coefficient === 1 ? variable : `${coefficient}${variable}`;
}

function formulaLatex(data) {
  return `${data.perimeterSymbol}=${data.terms
    .map(({ coefficient, variable }) => termLatex(coefficient, variable))
    .join("+")}`;
}

function otherTermsLatex(data, sign = "-") {
  return data.terms
    .filter(({ variable }) => variable !== data.solveVariable)
    .map(({ coefficient, variable }) => `${sign}${termLatex(coefficient, variable)}`)
    .join("");
}

function correctLatex(data) {
  const numerator = `${data.perimeterSymbol}${otherTermsLatex(data)}`;
  return data.solveCoefficient === 1
    ? numerator
    : `\\frac{${numerator}}{${data.solveCoefficient}}`;
}

function createOriginalTask() {
  const base = {
    perimeterSymbol: "L",
    shape: "concavePentagon",
    sideLabels: ["a", "c", "a", "b", "b"],
    terms: [
      { coefficient: 2, variable: "a" },
      { coefficient: 2, variable: "b" },
      { coefficient: 1, variable: "c" },
    ],
    solveVariable: "a",
    solveCoefficient: 2,
  };

  return {
    type: "E8PerimeterFormulaTransformClosedTask",
    displayBaseNumber: 5,
    data: {
      ...base,
      options: [
        { label: "A", latex: "\\frac{L-2b-c}{2}" },
        { label: "B", latex: "\\frac{L-2b+c}{2}" },
        { label: "C", latex: "L+2b-c" },
        { label: "D", latex: "L-2b-c" },
      ],
      correctIndex: 0,
    },
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 5,
    },
    tags: ["e8", "algebra", "przeksztalcanie_wzorow", "obwod"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  const shape = pick(Object.keys(SHAPES));
  const shapeTemplate = SHAPES[shape];
  const variables = shuffle(["a", "b", "c"]);
  const coefficients = pick(shapeTemplate.coefficientSets);
  const terms = variables.map((variable, index) => ({
    variable,
    coefficient: coefficients[index],
  }));
  const solvableTerms = terms.filter(({ coefficient }) => coefficient > 1);
  const solvedTerm = pick(solvableTerms);
  const base = {
    perimeterSymbol: pick(["L", "P"]),
    shape,
    terms,
    solveVariable: solvedTerm.variable,
    solveCoefficient: solvedTerm.coefficient,
  };
  const sideLabels = shuffle(
    terms.flatMap(({ coefficient, variable }) =>
      Array.from({ length: coefficient }, () => variable)
    )
  );
  const otherTerms = terms.filter(
    ({ variable }) => variable !== solvedTerm.variable
  );
  const firstOther = otherTerms[0];
  const wrongSignNumerator = `${base.perimeterSymbol}+${termLatex(
    firstOther.coefficient,
    firstOther.variable
  )}${otherTerms
    .slice(1)
    .map(({ coefficient, variable }) => `-${termLatex(coefficient, variable)}`)
    .join("")}`;
  const correct = correctLatex(base);
  const optionLatex = shuffle([
    correct,
    `\\frac{${wrongSignNumerator}}{${base.solveCoefficient}}`,
    `${base.perimeterSymbol}${otherTermsLatex(base)}`,
    `${base.perimeterSymbol}${otherTermsLatex(base, "+")}`,
  ]);

  return {
    type: "E8PerimeterFormulaTransformClosedTask",
    displayBaseNumber: 5,
    data: {
      ...base,
      sideLabels,
      options: optionLatex.map((latex, index) => ({
        label: LETTERS[index],
        latex,
      })),
      correctIndex: optionLatex.indexOf(correct),
    },
    source: { variant: "random", basedOn: "E8_2025_MAJ_5" },
    tags: ["e8", "algebra", "przeksztalcanie_wzorow", "obwod"],
  };
}

function ShapeDiagram({ data }) {
  const shape = SHAPES[data.shape];
  return (
    <svg
      viewBox="0 0 195 150"
      width="230"
      height="176"
      role="img"
      aria-label="Figura z opisanymi bokami"
      style={{ display: "block", margin: "8px auto 14px" }}
    >
      <polygon
        points={shape.points}
        fill="color-mix(in srgb, var(--color-accent, #f97316) 70%, #fff)"
        fillOpacity="0.9"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      {shape.labels.map((position, index) => (
        <SvgMathLabel
          key={`${position.x}-${position.y}`}
          x={position.x}
          y={position.y}
          math={data.sideLabels[index]}
          fontSize={14}
          color="currentColor"
        />
      ))}
    </svg>
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left" }}>
      <p>
        Obwód figury przedstawionej na rysunku wyraża się wzorem{" "}
        <InlineMath math={formulaLatex(data)} />.
      </p>
      <ShapeDiagram data={data} />
      <p>
        <strong>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</strong>
      </p>
      <p>
        Wielkość <InlineMath math={data.solveVariable} /> wyznaczoną poprawnie
        z podanego wzoru opisuje równanie
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: "8px",
        }}
      >
        {data.options.map((option) => (
          <div key={option.label}>
            <ChoiceOptionMath letter={option.label} math={`${data.solveVariable}=${option.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({
        value: index,
        label: option.label,
        textNode: option.label,
      }))}
      value={userAnswer?.choiceIndex ?? null}
      onChange={(choice) => {
        const choiceIndex =
          typeof choice === "number"
            ? choice
            : Number(choice?.target?.value ?? choice?.value);
        onChange({ choiceIndex });
      }}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer?.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const option = data.options[data.correctIndex];
  return (
    <ChoiceOptionMath letter={option.label} math={`${data.solveVariable}=${option.latex}`} />
  );
}

export function renderUserAnswer(userAnswer, task) {
  const option = task?.data?.options?.[userAnswer?.choiceIndex];
  return option ? (
    <ChoiceOptionMath letter={option.label} math={`${task.data.solveVariable}=${option.latex}`} />
  ) : (
    <i>(brak odpowiedzi)</i>
  );
}

export function renderExplanation({ data }) {
  const solvedTerm = termLatex(data.solveCoefficient, data.solveVariable);
  const numerator = `${data.perimeterSymbol}${otherTermsLatex(data)}`;
  const correctOption = data.options[data.correctIndex];

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1.</strong> Zaznaczamy składnik zawierający wyznaczaną
        zmienną <InlineMath math={data.solveVariable} />.
      </p>
      <BlockMath
        math={`${formulaLatex(data)},\\qquad ${solvedTerm}\\ \\text{zawiera zmienną }${data.solveVariable}`}
      />
      <p>
        <strong>Krok 2.</strong> Przenosimy pozostałe składniki na drugą stronę
        wzoru, zmieniając ich znaki.
      </p>
      <BlockMath math={`${solvedTerm}=${numerator}`} />
      {data.solveCoefficient > 1 && (
        <>
          <p>
            <strong>Krok 3.</strong> Dzielimy obie strony przez współczynnik
            stojący przy zmiennej.
          </p>
          <BlockMath
            math={`${data.solveVariable}=\\frac{${numerator}}{${data.solveCoefficient}}`}
          />
        </>
      )}
      <p>
        <strong>Krok 4.</strong> Porównujemy otrzymany wzór z odpowiedziami.
        Poprawna jest odpowiedź{" "}
        <ChoiceOptionMath letter={correctOption.label} math={`${data.solveVariable}=${correctOption.latex}`} />
        .
      </p>
    </div>
  );
}
